// PerformanceWarningDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import { Warning, Info } from '@mui/icons-material';

const PerformanceWarningDialog = ({ 
  open, 
  onClose, 
  onContinue, 
  warningData 
}) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: 'warning.light',
        color: 'warning.contrastText'
      }}>
        <Warning />
        Performance Analysis Period Active
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          icon={<Info />}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            This member's group has an active performance analysis for an ended period.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="text.primary">
            Active Performance Period Details:
          </Typography>
          
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Period For:
                </Typography>
                <Chip 
                  label={warningData?.active_period?.period_for || 'N/A'} 
                  color="primary" 
                  size="small"
                />
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Period Start:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {warningData?.active_period?.period_start ? 
                    formatDate(warningData.active_period.period_start) : 'N/A'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Period End:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {warningData?.active_period?.period_end ? 
                    formatDate(warningData.active_period.period_end) : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ 
          p: 2, 
          bgcolor: 'info.light', 
          borderRadius: 1,
          mb: 2
        }}>
          <Typography variant="body2" color="info.contrastText">
            <strong>Important:</strong> {warningData?.message}
          </Typography>
        </Box>

        <Box sx={{ 
          p: 2, 
          bgcolor: 'background.paper',
          border: '1px dashed',
          borderColor: 'warning.main',
          borderRadius: 1
        }}>
          <Typography variant="body2" color="text.secondary">
            <strong>What happens if you continue:</strong>
            <br />
            • This allocation will be recorded for the ended period ({warningData?.active_period?.period_for})
            <br />
            • The allocation date will be set to: {warningData?.active_period?.period_end ? 
              formatDate(warningData.active_period.period_end) : 'N/A'}
            <br />
            • If this allocation is meant for the current open period, please close the active performance analysis first
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          color="inherit"
          size="large"
        >
          Cancel & Exit
        </Button>
        <Button 
          onClick={onContinue}
          variant="contained"
          color="warning"
          size="large"
          sx={{ minWidth: 140 }}
        >
          Continue Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PerformanceWarningDialog;